import type { SelectOption } from '@/components/SearchableSelect'
import type {
  MonthlyPerformanceCheck,
  MonthlyPerformanceCommentary,
  MonthlyPerformanceGenerationStepKey,
  MonthlyPerformanceImportRef,
  MonthlyPerformanceMonthSummary,
  MonthlyPerformanceProjectionCase,
  MonthlyPerformanceReadiness,
  MonthlyPerformanceReport,
  MonthlyPerformanceReportSummary,
} from '@/types/monthlyPerformance'
import { formatReportMonthLabel } from '@/pages/distribution-waterfall/allocationMonthReadinessUtils'
import { formatDate, formatMonthLabel } from '@/pages/projections/projectionsFormat'
import { waterfallMonthSelectStatus } from '@/pages/distribution-waterfall/waterfallJeStatus'

export const NONE_PROJECTION_CASE_ID = 'none'

export function formatYyyyMmDd(iso: string | null | undefined): string {
  if (!iso?.trim()) return ''
  const trimmed = iso.trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed.slice(0, 10)
  }
  return formatDate(iso)
}

export function formatProjectionCaseOptionLabel(name: string, caseSavedAt: string | null): string {
  const saved = formatYyyyMmDd(caseSavedAt)
  if (saved) return `${name} · saved ${saved}`
  return name
}

export function formatProjectionMetaLine(
  source: MonthlyPerformanceReport['projection_source'],
): string {
  if (!source?.name?.trim()) return 'Projection: none'
  const captured = formatYyyyMmDd(source.captured_at)
  if (captured) return `Projection: ${source.name} · captured ${captured}`
  return `Projection: ${source.name}`
}

export function defaultProjectionCaseId(
  cases: MonthlyPerformanceProjectionCase[],
  reportCaseId: number | null | undefined,
): string {
  if (typeof reportCaseId === 'number') return String(reportCaseId)
  const defaultCase = cases.find((row) => row.is_default)
  if (defaultCase?.has_snapshot) return String(defaultCase.case_id)
  return NONE_PROJECTION_CASE_ID
}

export function defaultProjectionCaseMissingSnapshot(
  cases: MonthlyPerformanceProjectionCase[],
): boolean {
  const defaultCase = cases.find((row) => row.is_default)
  return Boolean(defaultCase && !defaultCase.has_snapshot)
}

export const GENERATION_STEPS: ReadonlyArray<{
  key: MonthlyPerformanceGenerationStepKey
  label: string
}> = [{ key: 'render', label: 'Rendering preview' }] as const

export type GenerationStepStatus = 'pending' | 'running' | 'done' | 'failed'

export function lastFullCalendarMonth(): string {
  const now = new Date()
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  return `${d.getFullYear()}-${month}`
}

export function defaultReportMonth(months: MonthlyPerformanceMonthSummary[]): string | null {
  if (!months.length) return null
  const ids = months.map((row) => row.month)
  const lastFull = lastFullCalendarMonth()
  if (ids.includes(lastFull)) return lastFull
  return months[months.length - 1]?.month ?? null
}

export const FIRST_MEMBERS_REPORT_MONTH = '2026-08'

export function monthStatusLabel(row: MonthlyPerformanceMonthSummary): string {
  if (row.month < FIRST_MEMBERS_REPORT_MONTH) return 'N/A'
  if (row.latest_report?.any_sent) return 'Sent'
  if (row.latest_report?.final_id) return 'Final'
  if (row.latest_report?.status === 'draft') return 'Draft'
  if (row.ready) return 'Ready'
  return 'Not ready'
}

export function monthOptionStatus(
  row: MonthlyPerformanceMonthSummary,
): SelectOption['status'] {
  if (row.month < FIRST_MEMBERS_REPORT_MONTH) return 'na'
  if (row.latest_report?.final_id) return 'complete'
  return waterfallMonthSelectStatus({
    jeStatus: 'none',
    checklistComplete: row.checklist_complete,
    checkedCount: row.checked_count,
    incomeAllocation: true,
  })
}

export function monthsToSelectOptions(months: MonthlyPerformanceMonthSummary[]): SelectOption[] {
  return [...months]
    .sort((a, b) => b.month.localeCompare(a.month))
    .map((row) => ({
      id: row.month,
      label: formatReportMonthLabel(row.month),
      status: monthOptionStatus(row),
      description: monthStatusLabel(row),
    }))
}

/** Latest sent report for a month (listReports is newest-first). */
export function latestSentReport(
  reports: MonthlyPerformanceReportSummary[],
): MonthlyPerformanceReportSummary | null {
  return reports.find((row) => row.status === 'sent') ?? null
}

/** Final draft for a month (listReports is newest-first). */
export function finalReport(
  reports: MonthlyPerformanceReportSummary[],
): MonthlyPerformanceReportSummary | null {
  return reports.find((row) => row.is_final) ?? null
}

/** History table: later of generated vs edited, with the matching actor. */
export function historyRowLastActivity(
  row: Pick<
    MonthlyPerformanceReportSummary,
    'generated_at' | 'generated_by' | 'edited_at' | 'edited_by'
  >,
): { at: string; by: string } {
  const generatedMs = Date.parse(row.generated_at)
  const editedMs = row.edited_at ? Date.parse(row.edited_at) : Number.NaN
  if (Number.isFinite(editedMs) && editedMs >= generatedMs) {
    return {
      at: row.edited_at as string,
      by: row.edited_by ?? row.generated_by,
    }
  }
  return { at: row.generated_at, by: row.generated_by }
}

/** Report HTML shown on the members view (listReports is newest-first). */
export function memberViewReport(
  reports: MonthlyPerformanceReportSummary[],
  options?: { includeDraftPreview?: boolean },
): MonthlyPerformanceReportSummary | null {
  if (options?.includeDraftPreview) {
    return reports[0] ?? null
  }
  const markedFinal = finalReport(reports)
  if (markedFinal?.status === 'sent') {
    return markedFinal
  }
  return latestSentReport(reports)
}

export const GENERATE_REPORT_HELP =
  "Build this month's report from the latest Projections historicals. Does not call the model."
export const REFRESH_PREVIEW_HELP =
  'Re-render the email from the saved commentary. Does not call the model.'
export const REWRITE_COMMENTARY_HELP =
  'Call the model to draft commentary. Numbers stay the same; only the wording changes.'
export const SEND_REPORT_HELP =
  'Choose test or members; members send lists the recipients before sending.'
export const COPY_HTML_DISABLED_HELP = 'Refresh the preview first.'

const CHICAGO_TZ = 'America/Chicago'

/** Chicago datetime for import lines: yyyy-mm-dd HH:MM (24-hour). */
export function formatChicagoImportTimestamp(
  value: string | null | undefined,
  empty = '—',
): string {
  if (!value?.trim()) return empty
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return empty
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: CHICAGO_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}`
}

export function formatLatestImportLine(importRef: MonthlyPerformanceImportRef): string {
  const lastClosed = importRef.last_closed_month
    ? formatMonthLabel(importRef.last_closed_month)
    : '—'
  const imported = formatChicagoImportTimestamp(importRef.imported_at)
  return `Latest import #${importRef.id} · last closed ${lastClosed} · imported ${imported}`
}

export function formatEmptyStateTitle(reportMonth: string): string {
  return `No report for ${formatMonthLabel(reportMonth)} yet.`
}

export function formatNotImportableMessage(
  reportMonth: string,
  lastClosedMonth: string | null | undefined,
): string {
  const monthLabel = formatMonthLabel(reportMonth)
  if (lastClosedMonth) {
    return `${monthLabel} is not in the latest import (last closed ${formatMonthLabel(lastClosedMonth)}). Update Historicals on Projections first.`
  }
  return `${monthLabel} is not in the latest import. Update Historicals on Projections first.`
}

export function formatImportMetaMutedLine(
  own: MonthlyPerformanceImportRef | null | undefined,
  latest: MonthlyPerformanceImportRef | null | undefined,
): string {
  const ownSegment = own
    ? `#${own.id} (${formatYyyyMmDd(own.imported_at)})`
    : 'unknown import'
  const latestSegment = latest
    ? `#${latest.id} (${formatYyyyMmDd(latest.imported_at)}, closes ${formatMonthLabel(latest.last_closed_month ?? '')})`
    : '—'
  return `Built from import ${ownSegment} · latest import ${latestSegment}`
}

export function formatDraftImportStaleBanner(params: {
  reportId: number
  own: MonthlyPerformanceImportRef | null | undefined
  latest: MonthlyPerformanceImportRef
  commentarySourceId: number
}): string {
  const ownSegment = params.own
    ? `#${params.own.id} (${formatYyyyMmDd(params.own.imported_at)})`
    : null
  const latestSegment = `#${params.latest.id} (${formatYyyyMmDd(params.latest.imported_at)}, closes ${formatMonthLabel(params.latest.last_closed_month ?? '')})`
  const usesImport = ownSegment
    ? `uses import ${ownSegment}`
    : 'uses an unknown import'
  return `Draft #${params.reportId} ${usesImport}. Historicals were updated in import ${latestSegment}. Regenerate creates a new draft from import #${params.latest.id} with the commentary from #${params.commentarySourceId}; this draft stays in history.`
}

export function formatStaleMembersSendTooltip(params: {
  reportId: number
  own: MonthlyPerformanceImportRef | null | undefined
  latest: MonthlyPerformanceImportRef | null | undefined
  commentarySourceId: number
}): string {
  if (!params.latest) {
    return 'Regenerate the report before sending to members.'
  }
  return formatDraftImportStaleBanner({
    reportId: params.reportId,
    own: params.own,
    latest: params.latest,
    commentarySourceId: params.commentarySourceId,
  })
}

export function formatGenerateButtonState(
  readiness: Pick<MonthlyPerformanceReadiness, 'can_generate' | 'blocking_reason'>,
  options?: {
    monthRow?: Pick<MonthlyPerformanceMonthSummary, 'month' | 'importable'> | null
    importRef?: MonthlyPerformanceImportRef | null
  },
) {
  const monthRow = options?.monthRow
  if (monthRow && monthRow.importable === false) {
    return {
      disabled: true,
      tooltip: formatNotImportableMessage(monthRow.month, options?.importRef?.last_closed_month),
    }
  }
  if (readiness.can_generate !== true) {
    return {
      disabled: true,
      tooltip: readiness.blocking_reason ?? 'Cannot generate report',
    }
  }
  return { disabled: false, tooltip: GENERATE_REPORT_HELP }
}

export function formatSendMembersButtonState(
  report: Pick<MonthlyPerformanceReport, 'can_send_members' | 'send_block_reasons'> | null | undefined,
) {
  if (!report || report.can_send_members !== true) {
    const reasons = report?.send_block_reasons ?? []
    return {
      disabled: true,
      tooltip: reasons.length > 0 ? reasons.join('; ') : 'Send to members is not available',
    }
  }
  return { disabled: false, tooltip: '' }
}

/** Audit §6.5 — human titles for passing report checks. */
export const CHECK_TITLES: Record<string, string> = {
  history_missing_month: 'Month is imported',
  cf_history_missing: 'Cash flow history is present',
  cf_ties_to_bs: 'Cash flow ties to balance sheet',
  lines_foot: 'Summary lines foot',
  history_import_stale: 'Historicals are current',
  labor_history_missing: 'Labor history is present',
  store_not_open_in_period: 'Stores are open in comparison periods',
  readiness_net_income_drift: 'Net income matches income allocation',
  commentary_unverified: 'Commentary figures are verified',
  cash_below_policy_floor: 'Cash vs projections cash floor',
}

/** Audit §6.5 — human titles when a check is failing. */
export const CHECK_FAIL_TITLES: Record<string, string> = {
  history_missing_month: 'Month not imported',
  cf_history_missing: 'Cash flow history missing',
  cf_ties_to_bs: 'Cash flow does not tie to balance sheet',
  lines_foot: 'Summary lines do not foot',
  history_import_stale: 'Historicals updated since generation',
  labor_history_missing: 'Labor history missing',
  store_not_open_in_period: 'Store not open in a comparison period',
  readiness_net_income_drift: 'Net income differs from the income allocation',
  commentary_unverified: 'Commentary has unverified figures',
  cash_below_policy_floor: 'Cash below projections cash floor',
}

export function formatCheckTitle(key: string, ok = true): string {
  const mapped = ok ? CHECK_TITLES[key] : CHECK_FAIL_TITLES[key]
  if (mapped) return mapped
  const spaced = key.trim().replace(/_/g, ' ')
  if (!spaced) return key
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

export function sortReportChecks(checks: MonthlyPerformanceCheck[]): MonthlyPerformanceCheck[] {
  const severityRank = (check: MonthlyPerformanceCheck) => {
    if (check.severity === 'blocking') return 0
    return 1
  }
  return [...checks].sort((a, b) => severityRank(a) - severityRank(b))
}

export interface UnverifiedFieldMatch {
  path: string
  text: string
  reason: string
}

function normalizeSentence(value: string): string {
  return value.trim().replace(/\s+/g, ' ')
}

function commentaryTextList(
  items: Array<{ text?: string }> | null | undefined,
): string[] {
  if (!items?.length) return []
  return items.map((item) => item.text ?? '')
}

function commentaryByCut(
  byCut: MonthlyPerformanceCommentary['by_cut'] | null | undefined,
): MonthlyPerformanceCommentary['by_cut'] {
  return {
    river_oaks: byCut?.river_oaks ?? '',
    highland_park: byCut?.highland_park ?? '',
    ga: byCut?.ga ?? '',
    total: byCut?.total ?? '',
  }
}

export function commentaryEquals(
  a: MonthlyPerformanceCommentary | null | undefined,
  b: MonthlyPerformanceCommentary | null | undefined,
): boolean {
  if (!a && !b) return true
  if (!a || !b) return false
  const cutA = commentaryByCut(a.by_cut)
  const cutB = commentaryByCut(b.by_cut)
  if (a.headline !== b.headline) return false
  if (cutA.river_oaks !== cutB.river_oaks) return false
  if (cutA.highland_park !== cutB.highland_park) return false
  if (cutA.ga !== cutB.ga) return false
  if (cutA.total !== cutB.total) return false
  const takeawaysA = commentaryTextList(a.takeaways)
  const takeawaysB = commentaryTextList(b.takeaways)
  if (takeawaysA.length !== takeawaysB.length) return false
  for (let i = 0; i < takeawaysA.length; i += 1) {
    if (takeawaysA[i] !== takeawaysB[i]) return false
  }
  const watchA = commentaryTextList(a.watch_items)
  const watchB = commentaryTextList(b.watch_items)
  if (watchA.length !== watchB.length) return false
  for (let i = 0; i < watchA.length; i += 1) {
    if (watchA[i] !== watchB[i]) return false
  }
  const vsA = (a.vs_projection ?? '').trim()
  const vsB = (b.vs_projection ?? '').trim()
  if (vsA !== vsB) return false
  return true
}

export function commentaryWasAutoGenerated(report: MonthlyPerformanceReport): boolean {
  if (report.commentary_model) return true
  const draft = report.commentary_draft
  if (draft && typeof draft === 'object' && 'headline' in draft) {
    const headline = String((draft as MonthlyPerformanceCommentary).headline ?? '').trim()
    if (headline) return true
  }
  const finalHeadline = report.commentary_final?.headline?.trim()
  return Boolean(finalHeadline)
}

export function draftCommentaryButtonLabel(report: MonthlyPerformanceReport): 'Draft' | 'Re-draft' {
  return commentaryWasAutoGenerated(report) ? 'Re-draft' : 'Draft'
}

export function cloneCommentary(
  commentary: MonthlyPerformanceCommentary,
): MonthlyPerformanceCommentary {
  return {
    headline: commentary.headline,
    takeaways: commentary.takeaways.map((item) => ({ ...item })),
    watch_items: commentary.watch_items.map((item) => ({ ...item })),
    vs_projection: commentary.vs_projection ?? null,
    vs_projection_cites: commentary.vs_projection_cites?.map((cite) => ({ ...cite })),
    by_cut: { ...commentary.by_cut },
  }
}

export function mapUnverifiedSentences(
  commentary: MonthlyPerformanceCommentary | null | undefined,
  unverified: Array<string | { sentence?: string; reason?: string; text?: string }> | null | undefined,
): UnverifiedFieldMatch[] {
  if (!commentary || !unverified?.length) return []

  const entries: Array<{ sentence: string; reason: string }> = unverified.map((item) => {
    if (typeof item === 'string') {
      return { sentence: item, reason: 'Could not verify numeric claims in this sentence' }
    }
    const sentence = item.sentence ?? item.text ?? ''
    return {
      sentence,
      reason: item.reason ?? 'Could not verify numeric claims in this sentence',
    }
  })

  const matches: UnverifiedFieldMatch[] = []
  const fields: Array<{ path: string; text: string }> = [
    { path: 'headline', text: commentary.headline },
    ...commentary.takeaways.map((item, index) => ({
      path: `takeaways.${index}`,
      text: item.text,
    })),
    ...commentary.watch_items.map((item, index) => ({
      path: `watch_items.${index}`,
      text: item.text,
    })),
    { path: 'vs_projection', text: commentary.vs_projection ?? '' },
    { path: 'by_cut.river_oaks', text: commentary.by_cut.river_oaks },
    { path: 'by_cut.highland_park', text: commentary.by_cut.highland_park },
    { path: 'by_cut.ga', text: commentary.by_cut.ga },
    { path: 'by_cut.total', text: commentary.by_cut.total },
  ]

  for (const field of fields) {
    const normalizedField = normalizeSentence(field.text)
    if (!normalizedField) continue
    for (const entry of entries) {
      const normalizedSentence = normalizeSentence(entry.sentence)
      if (!normalizedSentence) continue
      if (
        normalizedField === normalizedSentence ||
        normalizedField.includes(normalizedSentence) ||
        normalizedSentence.includes(normalizedField)
      ) {
        matches.push({ path: field.path, text: field.text, reason: entry.reason })
        break
      }
    }
  }

  return matches
}

export function getGenerationStepStatus(
  stepIndex: number,
  processedItems: number,
  jobStatus: 'pending' | 'running' | 'completed' | 'completed_with_errors' | 'failed' | 'cancelled' | 'idle',
): GenerationStepStatus {
  const isActive = jobStatus === 'pending' || jobStatus === 'running'
  const isFinished =
    jobStatus === 'completed' ||
    jobStatus === 'completed_with_errors' ||
    jobStatus === 'failed' ||
    jobStatus === 'cancelled'

  if (isFinished) {
    if (jobStatus === 'failed') {
      return processedItems > stepIndex ? 'done' : stepIndex === processedItems ? 'failed' : 'pending'
    }
    return processedItems > stepIndex ? 'done' : 'pending'
  }

  if (processedItems > stepIndex) return 'done'
  if (isActive && processedItems === stepIndex) return 'running'
  return 'pending'
}

export function humanPeriodLabel(reportMonth: string): string {
  return formatReportMonthLabel(reportMonth)
}

export function renderEmailSubject(subjectTemplate: string, reportMonth: string): string {
  const monthLabel = humanPeriodLabel(reportMonth)
  return subjectTemplate.replace(/\{month_label\}/g, monthLabel)
}
